import React, { useState, useEffect } from "react";
import SEO from "../seo";
const AddYourProducts = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "manual-add",
        "image-prompt-add",
        "import-products",
        "edit-delete",
        "quick-tips",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Add Your Products – xPAGE Help Center"
        description="Learn how to add, import, and manage products on your xPage store. Use AI tools to generate descriptions, import from marketplaces, and organize collections efficiently."
        keywords="xPAGE, add products, import products, AI product creation, eCommerce, product management, landing page builder"
        canonical="https://help.xpage.ai/help/landing-page-setup/Add%20Your%20Products"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <h1 className="text-systemTitle text-2xl font-semibold mb-3">
          Add Your Products
        </h1>

        {/* Introduction Paragraph */}
        <p className="text-sm text-systemLabel mb-6">
          With xPage, adding products is effortless. You can manually create
          listings, import from marketplaces like AliExpress or Amazon, or
          generate products from an image and prompt using AI. There's no limit,
          so you can scale your landing page with unlimited products and
          collections.
        </p>

        {/* Overview */}
        <section id="overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            In xPage, you can:
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>
                Add products manually with detailed descriptions, images, and
                pricing
              </li>
              <li>
                Import products via URL with AI-generated descriptions and
                properties
              </li>
              <li>
                Create products from an image and prompt for instant
                AI-generated listings
              </li>
              <li>Edit product details at any time</li>
              <li>Manage inventory, pricing, and shipping information</li>
            </ul>
          </div>
        </section>

        {/* Add a Product Manually */}
        <section id="manual-add" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Add a Product Manually
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              To add a product manually, log in to <strong>xPage Admin</strong>{" "}
              → <strong>Products → All Products</strong>, then click{" "}
              <strong>Add Product</strong>. Fill in the details:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mt-3">
              <li>Title: Name your product</li>
              <li>
                Description: Use AI-powered copy to generate conversion-focused
                text
              </li>
              <li>Images: Upload or generate visuals with AI</li>
              <li>Price: Set selling price and compare-at price</li>
              <li>
                SKU, Inventory, Weight & Dimensions for tracking and shipping
              </li>
              <li>Collections & Category for organization</li>
              <li>SEO Listing for search optimization</li>
              <li>Product Type: Choose physical or digital</li>
              <li>UGC: Upload UGC videos to increase conversions</li>
              <li>Reviews: Built-in reviews to build credibility</li>
              <li>Properties: Define product features and attributes</li>
              <li>Status: Active or Draft</li>
            </ul>

            <p className="text-sm text-systemLabel mt-3">
              Click <strong>Save</strong> to publish or{" "}
              <strong>Save as Draft</strong> to continue editing later.
            </p>
          </div>
        </section>

        {/* Add Product from Image + Prompt */}
        <section id="image-prompt-add" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Add a Product from Image + Prompt
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              xPage allows you to instantly create a product using an image and
              a short prompt.
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mt-3">
              <li>Navigate to Products → Add Product → From Image</li>
              <li>Upload your product image and enter a marketing prompt</li>
              <li>xPage AI generates title, description, and images</li>
              <li>Review and click Save</li>
            </ul>
          </div>
        </section>

        {/* Import Products */}
        <section id="import-products" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Import Products via URL
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              Navigate to <strong>Products → Import Product</strong>, paste the
              product URL, and choose your preferred language and marketing
              angle.
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel mt-3">
              <li>Click Import to pull images and product data</li>
              <li>Generate AI-optimized descriptions automatically</li>
              <li>Prepare product details for your landing page</li>
              <li>Review and click Save</li>
            </ul>
          </div>
        </section>

        {/* Edit / Delete */}
        <section id="edit-delete" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Edit or Delete Products
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>
                <strong>Edit:</strong> Go to Products → All Products, modify,
                then Save.
              </li>
              <li>
                <strong>Delete:</strong> Use the three-dot menu next to the
                product and select Delete.
              </li>
            </ul>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mt-3">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Tip:</span> Use xPage AI to
                refine descriptions, SEO, and product properties. Each product
                can have its own AI-enhanced landing page for better
                conversions.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Tips */}
        <section id="quick-tips" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Quick Tips
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel">
              <li>
                <strong>Use high-quality images:</strong> Products with clear
                visuals convert better.
              </li>
              <li>
                <strong>Leverage AI tools:</strong> Generate persuasive,
                on-brand copy.
              </li>
              <li>
                <strong>Organize collections:</strong> Make browsing simple and
                intuitive.
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#manual-add"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "manual-add"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Manual Add
              </a>
            </li>
            <li>
              <a
                href="#image-prompt-add"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "image-prompt-add"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Image + Prompt
              </a>
            </li>
            <li>
              <a
                href="#import-products"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "import-products"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Import Products
              </a>
            </li>
            <li>
              <a
                href="#edit-delete"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "edit-delete"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Edit & Delete
              </a>
            </li>
            <li>
              <a
                href="#quick-tips"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "quick-tips"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Quick Tips
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AddYourProducts;
